import { cancelInteractivityActionSnapshotMetadata } from './cancel/cancelInteractivityActionSnapshotMetadata';
import { closeSlidePopupInteractivityActionSnapshotMetadata } from './close-slide-popup/closeSlidePopupInteractivityActionSnapshotMetadata';
import { conditionalInteractivityActionSnapshotMetadata } from './conditional/conditionalInteractivityActionSnapshotMetadata';
import { controlAnimationInteractivityActionSnapshotMetadata } from './control-animation/controlAnimationInteractivityActionSnapshotMetadata';
import { effectInteractivityActionSnapshotMetadata } from './effect/effectInteractivityActionSnapshotMetadata';
import { goToSlideInteractivityActionSnapshotMetadata } from './go-to-slide/goToSlideInteractivityActionSnapshotMetadata';
import { hideElementsInteractivityActionSnapshotMetadata } from './hide-elements/hideElementsInteractivityActionSnapshotMetadata';
import { htmlPopupInteractivityActionSnapshotMetadata } from './html-popup/htmlPopupInteractivityActionSnapshotMetadata';
import { htmlTooltipInteractivityActionSnapshotMetadata } from './html-tooltip/htmlTooltipInteractivityActionSnapshotMetadata';
import { openLinkInteractivityActionSnapshotMetadata } from './open-link/openLinkInteractivityActionSnapshotMetadata';
import { parallelInteractivityActionSnapshotMetadata } from './parallel/parallelInteractivityActionSnapshotMetadata';
import { playAnimationInteractivityActionSnapshotMetadata } from './play-animation/playAnimationInteractivityActionSnapshotMetadata';
import { playAudioInteractivityActionSnapshotMetadata } from './play-audio/playAudioInteractivityActionSnapshotMetadata';
import { playStagedAnimationsInteractivityActionSnapshotMetadata } from './play-staged-animations/playStagedAnimationsInteractivityActionSnapshotMetadata';
import { sequenceInteractivityActionSnapshotMetadata } from './sequence/sequenceInteractivityActionSnapshotMetadata';
import { setVarInteractivityActionSnapshotMetadata } from './set-var/setVarInteractivityActionSnapshotMetadata';
import { showElementsInteractivityActionSnapshotMetadata } from './show-elements/showElementsInteractivityActionSnapshotMetadata';
import { slidePopupInteractivityActionSnapshotMetadata } from './slide-popup/slidePopupInteractivityActionSnapshotMetadata';
import { waitInteractivityActionSnapshotMetadata } from './wait/waitInteractivityActionSnapshotMetadata';
import { waitUntilInteractivityActionSnapshotMetadata } from './wait-until/waitUntilInteractivityActionSnapshotMetadata';
import { withTimeoutInteractivityActionSnapshotMetadata } from './with-timeout/withTimeoutInteractivityActionSnapshotMetadata';
import { zoomInteractivityActionSnapshotMetadata } from './zoom/zoomInteractivityActionSnapshotMetadata';
import type { InteractivityActionSnapshotOut } from './InteractivityAction';
import { InteractivityActionTypeValue } from './InteractivityActionTypeValue';
import { scrollToElementInteractivityActionSnapshotMetadata } from './scroll-to-element/scrollToElementInteractivityActionSnapshotMetadata';

export type GetIdsFromSnapshot<SN> = (snapshot: SN) => string[];

export type ReplaceIdsFromSnapshot<SN> = (
  snapshot: SN,
  replaceId: (id: string) => string,
) => void;

export interface InteractivityActionSnapshotMetadata {
  typeId: InteractivityActionTypeValue;

  innerInteractivityActions?: {
    fromSnapshot(sn: unknown): InteractivityActionSnapshotOut[];
  };

  replaceSlideIdsFromSnapshot?: ReplaceIdsFromSnapshot<any>;
  getSlideIdsFromSnapshot?: GetIdsFromSnapshot<any>;

  replaceItemIdsFromSnapshot?: ReplaceIdsFromSnapshot<any>;
  getItemIdsFromSnapshot?: GetIdsFromSnapshot<any>;

  replaceTargetRefIdsFromSnapshot?: ReplaceIdsFromSnapshot<any>;
}

const snapshotMetadatas: Record<
  InteractivityActionTypeValue,
  InteractivityActionSnapshotMetadata
> = {
  [InteractivityActionTypeValue.Cancel]: cancelInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.ControlAnimation]:
    controlAnimationInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.GoToSlide]: goToSlideInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.HideElements]:
    hideElementsInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.HtmlPopup]: htmlPopupInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.SlidePopup]:
    slidePopupInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.HtmlTooltip]:
    htmlTooltipInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.OpenLink]: openLinkInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Parallel]: parallelInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.PlayAnimation]:
    playAnimationInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.PlayAudio]: playAudioInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.PlayStagedAnimations]:
    playStagedAnimationsInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Sequence]: sequenceInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.ShowElements]:
    showElementsInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Wait]: waitInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.WithTimeout]:
    withTimeoutInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Zoom]: zoomInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.CloseSlidePopup]:
    closeSlidePopupInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Effect]: effectInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.SetVar]: setVarInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.Conditional]:
    conditionalInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.WaitUntil]: waitUntilInteractivityActionSnapshotMetadata,
  [InteractivityActionTypeValue.ScrollToElement]:
    scrollToElementInteractivityActionSnapshotMetadata,
};

export function tryGetInteractivityActionSnapshotMetadata(
  interactivityActionType: InteractivityActionTypeValue,
): InteractivityActionSnapshotMetadata | undefined {
  const metadata = snapshotMetadatas[interactivityActionType];

  return metadata;
}

export function getInteractivityActionSnapshotMetadata(
  interactivityActionType: InteractivityActionTypeValue,
): InteractivityActionSnapshotMetadata {
  const metadata = tryGetInteractivityActionSnapshotMetadata(interactivityActionType);

  if (!metadata) {
    throw new Error(
      `error getting snapshot metadata for unknown interactivity action type - ${interactivityActionType}`,
    );
  }

  return metadata;
}
